import React from 'react';

import { useSlug } from '../hooks/useSlug';
import { Image, ImageProps } from '../Image';
import type { GlobalProps } from '../types';
import { classes } from '../utils';

import styles from './StoryCard.module.css';

export enum TeaseType {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  Image = 'image',
  Video = 'video',
}

interface ImageTease {
  type: TeaseType.Image;
  value: Pick<
    ImageProps,
    | 'altText'
    | 'aspectRatio'
    | 'height'
    | 'origWidth'
    | 'origHeight'
    | 'src'
    | 'width'
  >;
}

type Tease = ImageTease;

export type StoryCardProps = GlobalProps & {
  byline?: string;
  headline: string;
  tease?: Tease;
  url: string;
  onClick?: () => void;
};

export const StoryCard = React.forwardRef<HTMLAnchorElement, StoryCardProps>(
  function StoryCardComponent(props: StoryCardProps, ref) {
    const {
      byline,
      className,
      headline,
      name = 'StoryCard',
      onClick,
      tease,
      unstyled,
      url,
    } = props;

    const componentName = useSlug(name);

    const cx = classes([styles.storyCard, className || ''], {
      unstyled,
      name,
    });

    return (
      <a
        className={cx}
        data-component={componentName}
        data-testid={componentName}
        href={url}
        onClick={onClick}
        ref={ref}
      >
        <article>
          {tease?.type === TeaseType.Image ? (
            <Image
              className="image"
              src={tease.value.src}
              altText={tease.value.altText}
              origWidth={tease.value.origWidth}
              origHeight={tease.value.origHeight}
              aspectRatio={tease.value.aspectRatio}
              width={tease.value.width}
              height={tease.value.height}
              isLazy
            />
          ) : null}

          <div>
            <p className="headline" data-testid={`${componentName}--headline`}>
              {headline}
            </p>
            {byline && (
              <p
                className="byline"
                data-testid={`${componentName}--byline`}
              >{`by ${byline}`}</p>
            )}
          </div>
        </article>
      </a>
    );
  }
);
